import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useDocumentTitle } from '../lib/branding';
import { Segmented } from '../components/Panel';
import { Notice } from '../components/Status';
import type { Role, Theme } from '@shared/protocol';

const roleLabel: Record<Role, string> = { admin: 'administrator', operator: 'operator', user: 'user' };

export function AccountPage() {
  useDocumentTitle('Account');
  const { user, setTheme } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [msg, setMsg] = useState<{ level: 'pass' | 'fail'; text: string } | null>(null);

  const change = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      await api('POST', '/api/me/password', { current, next });
      setCurrent('');
      setNext('');
      setMsg({ level: 'pass', text: 'Password changed. Other sessions have been signed out.' });
    } catch (err) {
      setMsg({ level: 'fail', text: err instanceof Error ? err.message : 'Could not change the password.' });
    }
  };

  return (
    <div className="page narrow">
      <div className="page-header">
        <div>
          <h1>Account</h1>
          <p className="muted">
            {user?.email}, {user ? roleLabel[user.role] : ''}
          </p>
        </div>
      </div>
      <section className="card" style={{ marginBottom: 24 }}>
        <header className="card-header">
          <h2>Appearance</h2>
        </header>
        <div className="card-body">
          <Segmented<Theme>
            label="Theme"
            value={user?.theme ?? 'light'}
            onChange={(t) => void setTheme(t)}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark (control room)' },
            ]}
          />
          <p className="muted small" style={{ marginTop: 8 }}>
            Saved to your account and used on any device you sign in from.
          </p>
        </div>
      </section>
      <form className="card" onSubmit={change}>
        <header className="card-header">
          <h2>Change password</h2>
        </header>
        <div className="card-body">
          <div className="field">
            <label htmlFor="cur">Current password</label>
            <input id="cur" className="input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
          </div>
          <div className="field">
            <label htmlFor="new">New password</label>
            <input id="new" className="input" type="password" minLength={10} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required />
            <span className="hint">At least 10 characters.</span>
          </div>
          {msg && <Notice level={msg.level}>{msg.text}</Notice>}
        </div>
        <footer className="modal-footer">
          <button type="submit" className="btn btn-primary">
            Change password
          </button>
        </footer>
      </form>
    </div>
  );
}
